// frontend/modules/customer-notification/stores/notification-store.ts
import { create } from 'zustand';
import { CustomerNotificationHistory, NotificationCategoryType, NotificationStatistics } from '@/shared/api/model';
import { NotificationPlatforms } from '@/shared/api/model';

interface NotificationState {
    // History state
    notifications: CustomerNotificationHistory[];
    filteredNotifications: CustomerNotificationHistory[];
    isLoading: boolean;
    error: string | null;
    
    // Filter state
    searchTerm: string;
    selectedBranch: string | null;
    selectedPlatform: NotificationPlatforms | null;
    selectedCategory: NotificationCategoryType | null;
    startDate: Date | null;
    endDate: Date | null;
    successStatus: boolean | null;
    
    // Statistics state
    statistics: NotificationStatistics | null;
    
    // Form state for triggering notifications
    isFormVisible: boolean;
    activeFormType: string | null;
    
    // Actions
    setNotifications: (notifications: CustomerNotificationHistory[]) => void;
    setFilteredNotifications: (notifications: CustomerNotificationHistory[]) => void;
    setIsLoading: (isLoading: boolean) => void;
    setError: (error: string | null) => void;
    setSearchTerm: (term: string) => void;
    setSelectedBranch: (branch: string | null) => void;
    setSelectedPlatform: (platform: NotificationPlatforms | null) => void;
    setSelectedCategory: (category: NotificationCategoryType | null) => void;
    setDateRange: (startDate: Date | null, endDate: Date | null) => void;
    setSuccessStatus: (status: boolean | null) => void;
    setStatistics: (statistics: NotificationStatistics | null) => void;
    setIsFormVisible: (visible: boolean) => void;
    setActiveFormType: (formType: string | null) => void;
    resetFilters: () => void;
    filterNotifications: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
    // Initial state
    notifications: [],
    filteredNotifications: [],
    isLoading: false,
    error: null,
    searchTerm: '',
    selectedBranch: null,
    selectedPlatform: null,
    selectedCategory: null,
    startDate: null,
    endDate: null,
    successStatus: null,
    statistics: null,
    isFormVisible: false,
    activeFormType: null,
    
    // Actions
    setNotifications: (notifications) => set({ 
        notifications, 
        filteredNotifications: notifications 
    }),
    
    setFilteredNotifications: (filteredNotifications) => set({
        filteredNotifications
    }),
    
    setIsLoading: (isLoading) => set({ isLoading }),
    
    setError: (error) => set({ error }),
    
    setSearchTerm: (searchTerm) => {
        set({ searchTerm });
        get().filterNotifications();
    },
    
    setSelectedBranch: (selectedBranch) => {
        set({ selectedBranch });
        get().filterNotifications();
    },
    
    setSelectedPlatform: (selectedPlatform) => {
        set({ selectedPlatform });
        get().filterNotifications();
    },
    
    setSelectedCategory: (selectedCategory) => {
        set({ selectedCategory });
        get().filterNotifications();
    },
    
    setDateRange: (startDate, endDate) => {
        set({ startDate, endDate });
        get().filterNotifications();
    },
    
    setSuccessStatus: (successStatus) => {
        set({ successStatus });
        get().filterNotifications();
    },
    
    setStatistics: (statistics) => set({ statistics }),
    
    setIsFormVisible: (isFormVisible) => set({ isFormVisible }),
    
    setActiveFormType: (activeFormType) => set({ activeFormType }),
    
    resetFilters: () => {
        set({
            searchTerm: '',
            selectedBranch: null,
            selectedPlatform: null,
            selectedCategory: null,
            startDate: null,
            endDate: null,
            successStatus: null
        });
        get().filterNotifications();
    },
    
    filterNotifications: () => {
        const {
            notifications,
            searchTerm,
            selectedBranch,
            selectedPlatform,
            selectedCategory,
            startDate,
            endDate,
            successStatus
        } = get();
        
        let filtered = [...notifications];
        
        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(notification =>
                notification.templateName?.toLowerCase().includes(term) ||
                notification.recipient?.toLowerCase().includes(term) ||
                notification.customerNumber?.toLowerCase().includes(term) ||
                notification.messageContent?.toLowerCase().includes(term)
            );
        }
        
        // Filter by branch
        if (selectedBranch) {
            filtered = filtered.filter(notification =>
                notification.branch === selectedBranch
            );
        }
        
        // Filter by platform
        if (selectedPlatform) {
            filtered = filtered.filter(notification =>
                notification.platform === selectedPlatform
            );
        }
        
        // Filter by category
        if (selectedCategory) {
            filtered = filtered.filter(notification =>
                notification.category === selectedCategory
            );
        }
        
        // Filter by date range
        if (startDate) {
            filtered = filtered.filter(notification =>
                notification.sentAt && new Date(notification.sentAt) >= startDate
            );
        }
        
        if (endDate) {
            const nextDay = new Date(endDate);
            nextDay.setDate(nextDay.getDate() + 1);
            
            filtered = filtered.filter(notification =>
                notification.sentAt && new Date(notification.sentAt) < nextDay
            );
        }
        
        // Filter by success status
        if (successStatus !== null) {
            filtered = filtered.filter(notification =>
                notification.success === successStatus
            );
        }
        
        set({ filteredNotifications: filtered });
    }
}));
